import { type ReactElement } from 'react';
import { HighlightBox } from '@/components/shared/HighlightBox';
import { Button } from '@/components/ui/button';
import { ExternalLink, Info, TriangleAlert } from 'lucide-react';

/**
 * Install / configure / uninstall body for the Kirin VSCode Copilot extension,
 * including the "Verify GitHub Copilot in VSCode Hooks" guidance. Rendered inside
 * the "Kirin for VSCode Copilot" setup frame on the Get Started screen.
 *
 * The whole frame is gated behind the `hide_vscode_extension` flag by the caller
 * because the VSCode extension is being deprecated.
 */
export function VSCodeInstallSteps(): ReactElement {
  return (
    <>
      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          Install Kirin for VSCode Copilot
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="font-medium">Option A — Search in VSCode</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>Open VSCode → Extensions</li>
              <li>
                Search for <strong>"Kirin"</strong>
              </li>
              <li>
                Click <em>Install</em>
              </li>
            </ol>
          </div>
          <div className="space-y-3">
            <div className="font-medium">Option B — Direct Install</div>
            <Button asChild className="w-full whitespace-normal">
              <a
                href="https://marketplace.visualstudio.com/items?itemName=Knostic.kirin-vscode"
                target="_blank"
                rel="noopener noreferrer"
                className="flex min-w-0 items-center justify-center gap-2 text-center"
              >
                <ExternalLink className="h-4 w-4 flex-shrink-0" />
                <span className="min-w-0">
                  Download Kirin from the VSCode Marketplace
                </span>
              </a>
            </Button>
            <div className="text-xs text-muted-foreground">
              Click "Install" on the marketplace page to install the extension
              directly in VSCode
            </div>
          </div>
        </div>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          Configure & Verify
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="font-medium">Set Your API Key</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>In VSCode, open Command Palette (⌘/Ctrl+Shift+P)</li>
              <li>
                Run{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  Kirin: Set API Key
                </code>
              </li>
              <li>Paste your key and save</li>
            </ol>
          </div>
          <div className="space-y-3">
            <div className="font-medium">Test the Connection</div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>
                Open a project with Copilot config files (e.g.,{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  .github/copilot-instructions.md
                </code>
                )
              </li>
              <li>
                Run{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  Kirin: Scan Now
                </code>
              </li>
              <li>
                Check the <strong>Alerts</strong> tab for results
              </li>
            </ol>
          </div>
        </div>

        <HighlightBox variant="info" padding="lg" className="mt-6">
          <div className="text-sm">
            <div className="font-medium mb-2">What happens next?</div>
            <div className="text-muted-foreground">
              Kirin monitors GitHub Copilot configurations, MCP server
              definitions, and AI-generated code in real-time. Suspicious
              configurations are automatically quarantined. Use the{' '}
              <strong>Policies</strong> tab to customize security rules.
            </div>
          </div>
        </HighlightBox>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 flex items-center gap-2 break-words">
          Verify GitHub Copilot in VSCode Hooks
          <Info className="h-4 w-4 text-muted-foreground shrink-0" />
        </h3>
        <div className="text-sm text-muted-foreground space-y-2 mb-4">
          <p>
            GitHub moved Copilot in VSCode's hook functionality to an
            organization-level setting in the GitHub organization policies page.
            The setting is called <strong>"Editor preview features"</strong> and
            may now be disabled by default.
          </p>
          <p>
            Kirin enforces AI security policies through these hooks — if they're
            disabled, Kirin's protection won't function as intended. Please
            follow the steps below to verify your configuration:
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <div className="font-medium">
              Verify your GitHub organization settings (Admin)
            </div>
            <div className="text-sm space-y-2">
              <div>
                Enable <strong>"Editor preview features"</strong> for your
                account type:
              </div>
              <ul className="list-disc ml-5 space-y-1">
                <li>
                  <strong>Enterprise:</strong> AI Controls → Copilot → Editor
                  preview features
                </li>
                <li>
                  <strong>Organization:</strong> Settings → Copilot → Policies →
                  Editor preview features
                </li>
              </ul>
              <a
                href="https://docs.github.com/en/copilot/how-tos/administer-copilot/manage-for-organization/manage-policies"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary hover:underline"
              >
                <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
                GitHub Copilot organization policies
              </a>
            </div>
          </div>
          <div className="space-y-3">
            <div className="font-medium">
              Verify your individual VSCode settings (Developer)
            </div>
            <ol className="list-decimal ml-5 text-sm space-y-2">
              <li>Open GitHub Copilot in VSCode Chat Settings</li>
              <li>
                Search for <strong>Hooks</strong> or{' '}
                <code className="bg-slate-100 dark:bg-slate-800 px-1 rounded">
                  chat.useHooks
                </code>
              </li>
              <li>
                Ensure that <strong>Chat: Use Hooks</strong> is enabled
              </li>
            </ol>
            <div className="text-sm text-muted-foreground">
              If the Settings shows an <strong>"ORG"</strong> badge and greyed
              it, your organization's device management policy is overriding it
              — contact your IT admin.
            </div>
            <a
              href="https://code.visualstudio.com/docs/copilot/customization/hooks"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline text-sm"
            >
              <ExternalLink className="h-3.5 w-3.5 flex-shrink-0" />
              VSCode Copilot Hooks documentation
            </a>
          </div>
        </div>

        <HighlightBox
          variant="warning"
          padding="lg"
          className="mt-6"
          role="alert"
        >
          <div className="flex items-start gap-2 text-sm">
            <TriangleAlert className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
            <div>
              Please do not manually disable hooks settings — Kirin cannot
              enforce your AI security policies without them.
            </div>
          </div>
        </HighlightBox>
      </div>

      <div>
        <h3 className="text-base sm:text-lg font-semibold mb-4 break-words">
          To Uninstall:
        </h3>
        <ol className="list-decimal ml-5 text-sm space-y-2">
          <li>Open VSCode → Extensions</li>
          <li>
            Search for <strong>"Kirin"</strong>
          </li>
          <li>
            Click the gear icon next to the extension and select{' '}
            <strong>Uninstall</strong>
          </li>
        </ol>
      </div>
    </>
  );
}
